import {
   Button,
   Group,
   Modal,
   Skeleton,
   Text,
} from "@mantine/core";
import {
   IconDownload,
} from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
export const ACCEPTED_EXTENSIONS_PREVIEW_FILE_IMAGE_COMPONENT = [
   "jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"
];
export const ACCEPTED_EXTENSIONS_PREVIEW_FILE_DOCUMENT_COMPONENT = [
   "doc",
   "docx",
   "xls",
   "xlsx",
];
export const ACCEPTED_EXTENSIONS_COMPRESSED_FILE_COMPONENT = [
   "zip",
   "rar",
   "7z",
   "gz",
   "bz2",
   "tar"
];
export const CURRENT_DOMAIN = typeof window !== "undefined" ? window.location.origin : "";
import { useFetchFileUrl } from "utils/hooks/pizza";


export const getBaseFilename = (name: string | undefined | null) => {
   if (!name) return "";
   let clean = name.split("?")[0];
   if (clean.includes("/") || clean.includes("\\")) {
      clean = clean.split(/[/\\]/).pop() || clean;
   }
   return clean;
};

export const sanitizeUrl = (url: string | undefined | null) => {
   if (!url) return "";
   return url.replace(/\\u0026/g, '&').replace(/%5Cu0026/gi, '&');
};

const CellModalAttachment = ({
   filename,
   url,
   buttonDownload = true,
   alternateFilename = "",
}: {
   alternateFilename?: string;
   filename: string;
   url: string;
   buttonDownload?: boolean;
}) => {

   const [opened, { open, close }] = useDisclosure(false);
   const { fetchUrl, extFile, isLoading, error } = useFetchFileUrl({
      filename,
      url,
   });


   return (
      <>
         <Group >
            {
               filename ? (
                  <Button
                     variant="outline"
                     color="blue"
                     onClick={open}
                     className="max-w-[75%] truncate"
                     disabled={ACCEPTED_EXTENSIONS_COMPRESSED_FILE_COMPONENT.includes(extFile)}
                  >
                     {alternateFilename != "" ? alternateFilename : filename}
                  </Button>
               ) : (
                  <Text color="gray.6">No file has been uploaded yet</Text>
               )
            }

            {buttonDownload && filename && url && (
               <Button
                  onClick={async () => {
                     window.open(fetchUrl || "", "_blank");
                  }}
                  variant="filled"
                  size="xs"
                  radius="lg">
                  <IconDownload size={16} />
               </Button>
            )}
         </Group>
         <Modal
            opened={opened}
            onClose={close}
            radius={0}
            transitionProps={{ transition: "fade", duration: 200 }}
            size={'60%'}
            centered
            title={filename}>
            {!filename && !url && (
               <Skeleton visible={true} height={300} width="100%" />
            )}
            {filename && url && ACCEPTED_EXTENSIONS_PREVIEW_FILE_IMAGE_COMPONENT.includes(extFile || "") && (
               <IframeComponentPreviewImage url={url} filename={filename} />
            )}
            {filename && url && ACCEPTED_EXTENSIONS_PREVIEW_FILE_DOCUMENT_COMPONENT.includes(extFile || "") && (
               <IframeComponentPreviewDocs url={url} filename={filename} />
            )}
            {filename && url && extFile?.toLowerCase() === "pdf" && (
               <IframeComponentPreviewPDF url={url} filename={filename} />
            )}
            {filename && url && !ACCEPTED_EXTENSIONS_PREVIEW_FILE_IMAGE_COMPONENT.includes(extFile || "") && !ACCEPTED_EXTENSIONS_PREVIEW_FILE_DOCUMENT_COMPONENT.includes(extFile || "") && extFile?.toLowerCase() !== "pdf" && (
               <div className="flex flex-col items-center justify-center p-8 space-y-4 text-center">
                  <Text color="gray.7">
                     Preview tidak tersedia untuk format file ini ({extFile ? `.${extFile}` : "tidak diketahui"}).
                  </Text>
                  <Button
                     variant="outline"
                     color="blue"
                     onClick={() => window.open(fetchUrl || url, "_blank")}
                  >
                     Unduh File
                  </Button>
               </div>
            )}
         </Modal>
      </>
   );
};

export const IframeComponentPreviewImage = ({ url, filename }: { url: string; filename: string }) => {
   const { fetchUrl, extFile, isLoading, error } = useFetchFileUrl({
      filename,
      url,
   });
   return (
      <>
         <div style={{ height: "auto" }} className="flex justify-center items-center">
            <img
               src={fetchUrl || ""}
               alt={filename}
               className="max-h-[1000px] max-w-full object-contain"
            />
         </div>
      </>
   );
};

export const IframeComponentPreviewDocs = ({ url, filename }: { url: string; filename: string }) => {
   const { fetchUrl, extFile, isLoading, error } = useFetchFileUrl({
      filename,
      url,
   });

   const googleDocsUrl = `https://docs.google.com/gview?url=${encodeURIComponent(fetchUrl || "")}&embedded=true`;
   const excelProviderUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fetchUrl || "")}`;
   if (extFile === 'xlsx' || extFile === 'xls') {
      return (
         <div style={{ height: "auto" }} className="flex justify-center items-center">
            <iframe
               src={excelProviderUrl}
               width="100%"
               height="1000px"
               style={{ border: "none" }}
            ></iframe>
         </div>
      );
   }
   return (
      <>
         <div style={{ height: "auto" }}>
            <iframe
               src={googleDocsUrl}
               width="100%"
               height="1000px"
               style={{ border: "none" }}
            ></iframe>
         </div>
      </>
   );
};

export const IframeComponentPreviewPDF = ({ url, filename }: { url: string; filename: string }) => {
   const { fetchUrl, isLoading } = useFetchFileUrl({
      filename,
      url,
   });

   if (isLoading || !fetchUrl) {
      return <Skeleton visible={true} height={600} width="100%" />;
   }

   return (
      <div style={{ height: "auto" }} className="w-full flex flex-col items-center">
         <object
            data={fetchUrl}
            type="application/pdf"
            width="100%"
            height="600px"
            style={{ border: "none" }}
         >
            <iframe
               src={fetchUrl}
               width="100%"
               height="600px"
               style={{ border: "none" }}
            >
               <div className="flex flex-col items-center justify-center p-6 text-center space-y-3">
                  <Text color="gray.7">
                     Preview PDF tidak dapat ditampilkan secara langsung di perangkat ini.
                  </Text>
                  <Button
                     variant="outline"
                     color="blue"
                     onClick={() => window.open(fetchUrl, "_blank")}
                  >
                     Buka / Download PDF
                  </Button>
               </div>
            </iframe>
         </object>
      </div>
   );
};

export default CellModalAttachment;